import React from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { gbp } from "../../lib/apiClient";
import { Section } from "./layout";
import { useRowDrag } from "./useRowDrag";
import { MarkupCell } from "./MarkupCell";
import { numVal, parseNum } from "./utils";

/**
 * SundryLineTable — same structure as Parts & Materials but stored on `quote.sundries`.
 * Consumables, fixings, small parts.
 */
export function SundryLineTable({
  items,
  onChange,
  tmpId,
  previewItems,
  previewTotal,
  defaultMarkup,
  markupOptions = [],
  userInitials = "",
  isAdmin = false,
  minMarkup = null,
  storageKey,
}) {
  const sundDrag = useRowDrag(items || [], onChange, "sundries");
  const add = () => onChange([
    ...(items || []),
    {
      id: tmpId(),
      name: "",
      description: "",
      quantity: 1,
      supplier_cost: 0,
      markup_percent: defaultMarkup ?? 35,
    },
  ]);
  const setRow = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));
  const lineTotalFor = (idx, ln) => {
    const fromPreview = previewItems?.[idx]?.line_total;
    if (fromPreview != null) return fromPreview;
    const q = Number(ln.quantity || 0);
    const c = Number(ln.supplier_cost || 0);
    const m = Number(ln.markup_percent || 0) / 100;
    return q * c * (1 + m);
  };

  return (
    <Section
      title="Sundries"
      subtitle="Consumables, fixings, small parts — same structure as materials"
      collapsible
      storageKey={storageKey}
      action={
        <button
          onClick={add}
          className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:text-slate-900 text-slate-600"
          data-testid="add-sundry-button"
        >
          <Plus size={14} /> Add line
        </button>
      }
    >
      <div>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full text-sm table-fixed min-w-[640px]">
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "2%" }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th className="px-1 py-2"></th>
              {["Name", "Description", "Qty", "Cost to client", "Markup %", "Line Total", ""].map((h) => (
                <th key={h} className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(items || []).length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400 text-sm">No sundries yet.</td></tr>
            )}
            {(items || []).map((ln, idx) => (
              <tr key={ln.id || idx} className="border-b border-slate-200" {...sundDrag.dragProps(idx)}>
                <td className="px-1 py-1 align-middle text-slate-400 hover:text-slate-700" {...sundDrag.handleProps(idx)}>
                  <GripVertical size={14} />
                </td>
                <td className="px-1 py-1">
                  <input
                    value={ln.name || ""}
                    onChange={(e) => setRow(idx, { name: e.target.value })}
                    className="qb-input h-9"
                    data-testid={`sundry-name-${idx}`}
                    placeholder="e.g. Fixings"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    value={ln.description || ""}
                    onChange={(e) => setRow(idx, { description: e.target.value })}
                    className="qb-input h-9"
                    data-testid={`sundry-desc-${idx}`}
                    placeholder="Notes / detail"
                  />
                </td>
                <td className="px-1 py-1">
                  <input type="number" step="0.01"
                    value={numVal(ln.quantity)}
                    onChange={(e) => setRow(idx, { quantity: parseNum(e.target.value) })}
                    className="qb-input h-9 font-mono"
                    data-testid={`sundry-qty-${idx}`}
                  />
                </td>
                <td className="px-1 py-1">
                  <input type="number" step="0.01"
                    value={numVal(ln.supplier_cost)}
                    onChange={(e) => setRow(idx, { supplier_cost: parseNum(e.target.value) })}
                    className="qb-input h-9 font-mono"
                    data-testid={`sundry-cost-${idx}`}
                  />
                </td>
                <td className="px-1 py-1">
                  <MarkupCell
                    value={ln.markup_percent}
                    presets={markupOptions}
                    userInitials={userInitials}
                    overrideBy={ln.markup_override_by}
                    overrideAt={ln.markup_override_at}
                    onChangeWithAudit={(pct, audit) => setRow(idx, { markup_percent: pct, ...audit })}
                    testId={`sundry-markup-${idx}`}
                    isAdmin={isAdmin}
                    minPercent={minMarkup}
                  />
                </td>
                <td className="px-2 py-1 font-mono text-right whitespace-nowrap">{gbp(lineTotalFor(idx, ln))}</td>
                <td className="px-2 py-1 text-right">
                  <button
                    onClick={() => removeRow(idx)}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`remove-sundry-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {(items || []).length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm bg-slate-50 rounded-lg px-4 py-2">
          <span className="text-slate-600">Sundries total</span>
          <span className="font-mono font-semibold text-brand-blue" data-testid="sundry-total">{gbp(previewTotal || 0)}</span>
        </div>
      )}
    </Section>
  );
}
